import * as Sentry from '@sentry/browser';
import logErrorInDev from 'src/internal/common/logErrorInDev';
import {DEFAULT_OBSERVABILITY_CONFIG} from 'src/internal/constants';
import {captureException} from 'src/internal/errors/sentry/captureException';
import {
  createHubConfig,
  createHubIntegrations,
} from 'src/internal/errors/sentry/createHubConfig';
import {sanitizeExtras} from 'src/internal/errors/sentry/utils';
import buildErrorScope from 'src/internal/errors/utils/buildErrorScope';

import type {
  SentryHub,
  SentryHubOptions,
} from 'src/internal/errors/sentry/types';
import type {ErrorScope, IReports, Severity} from 'src/internal/errors/types';
import type {ObservabilityConfig} from 'src/types';

export default class ReportsServer implements IReports {
  #config: ObservabilityConfig;
  #hubs = new Map<string, SentryHub>();
  #hubConfig: SentryHubOptions;
  #mainHubIntegrations: SentryHubOptions['integrations'];

  constructor(config: ObservabilityConfig) {
    this.#config = {
      ...config,
      errors: {
        ...(config.errors ?? {}),
        projects: {
          ...(DEFAULT_OBSERVABILITY_CONFIG.errors?.projects ?? {}),
          ...(config.errors?.projects ?? {}),
        },
      },
    };
    this.#hubConfig = createHubConfig(this.#config);
    this.#mainHubIntegrations = createHubIntegrations(this.#config, true);

    const {project: mainProject, errors} = this.#config;
    const {projects = {}} = errors ?? {};

    // We need to create the main hub first, so that Sentry configures the unhandled
    // error handler.
    if (projects[mainProject]) {
      this.#hubs.set(mainProject, this.createHub(projects[mainProject], true));
    }

    for (const [project, dsn] of Object.entries(projects)) {
      if (project !== mainProject) {
        this.#hubs.set(project, this.createHub(dsn, false));
      }
    }

    const hub = this.#hubs.get(mainProject);

    // This shouldn't happen as we're using internal auto-generated IDs, but
    // JavaScript's Map type forces undefined values check.
    if (!hub) {
      logErrorInDev(`Missing hub ${mainProject}`, 'error');
    }
  }

  /**
   * Returns the config.project's hub.
   * Main sentry hub can be used for unhandled errors and other global events.
   */
  public getMainHub = (): SentryHub | undefined => {
    return this.#hubs.get(this.#config.project);
  };

  public error = (error: any, scope?: ErrorScope): void => {
    return this.report('error', error, scope);
  };

  public warning = (error: any, scope?: ErrorScope): void => {
    return this.report('warning', error, scope);
  };

  private report(
    level: Exclude<Severity, 'info'>,
    error: any,
    scope?: ErrorScope,
  ): void {
    const hub = this.#hubs.get(scope?.project ?? this.#config?.project);

    if (
      this.#config.errors?.disabled ||
      this.#config.errors?._internal_injected_methods_only ||
      !hub
    ) {
      return;
    }

    // Convert strings to errors to retrieve stack trace
    const normalizedError =
      typeof error === 'string' ? new Error(error) : error;

    const errorScope = buildErrorScope(this.#config, normalizedError, scope);

    captureException(hub, normalizedError, {
      tags: errorScope.tags,
      extras: errorScope.extras,
      level,
    });
  }

  private createHub(dsn: string, isMainHub = false): SentryHub {
    // "fetch" detection is buggy in our Sentry version, so make it ourselves.
    const transport =
      typeof fetch === 'function'
        ? Sentry.Transports.FetchTransport
        : Sentry.Transports.XHRTransport;

    const client = new Sentry.BrowserClient({
      dsn,
      release: this.#hubConfig.release,
      enabled: this.#hubConfig.enabled ?? true,
      integrations:
        (isMainHub
          ? this.#mainHubIntegrations
          : this.#hubConfig.integrations) ?? [],
      beforeSend: this.#hubConfig.beforeSend,
      transport,
    });

    const hub = new Sentry.Hub(client);

    hub.configureScope((scope) => {
      const {user, tags, extras} = this.#hubConfig.initialScope ?? {};

      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      user && scope.setUser(user);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      tags && scope.setTags(tags);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      extras && scope.setExtras(sanitizeExtras(extras));

      // 'error' is the implicit default that sentry uses, but we're setting it
      // here as the explicit default so that integrations have reliable access
      // to the level per event.
      scope.setLevel(Sentry.Severity.Error);
    });

    return hub;
  }
}
